import { Component, signal } from '@angular/core';
import { AngularModule } from '../../../../modules/angular.module';
import { Activity, LanguageCode, ProductStatus, TextUtils } from '@mira-avant/mtlibs';
import { ActivityService } from '../../../../services/activity.service';
import { NavigationService } from '../../../../services/navigation.service';
import { AdminService } from '../../../../services/admin.service';
import { ZorroModule } from '../../../../modules/zorro.module';
import { LanguageSelectorComponent } from '../../language-selector/language-selector.component';
import { ActivityFormComponent } from '../../../forms/standard/activity-form/activity-form.component';
import { ActivityText } from '@mira-avant/mtlibs';
import { v4 } from 'uuid';
import { ClipboardService } from '../../../../services/clipboard.service';
import { Subscription } from 'rxjs';
import { EMPTY_ACTIVITY, _ } from '@mira-avant/mtlibs';
import { InProgressService } from '../../../../services/in-progress.service';
import { TableFormContainer } from '../table-form-container/table-form-container';
import {
	CmsTable,
	Column,
	ENABLE_COPY_COLUMN,
	ENABLE_DELETE_COLUMN,
	ENABLE_EDIT_COLUMN,
	ENABLE_PREVIEW_COLUMN,
	ENABLE_DISPLAY_ORDER_COLUMN,
	ENABLE_UP_DOWN_COLUMN,
	ENABLE_CHECKBOX_COLUMN,
} from '../cms-table/cms-table';
import { RowColumn } from '../cms-table/row-column';
import { HeaderColumn } from '../cms-table/header-column';
import { SkillSummary } from '../skill-summary/skill-summary';
import { RubricLevelSummary } from '../rubric-level-summary/rubric-level-summary';
import { SkillService } from '../../../../services/skill.service';
import { RubricService } from '../../../../services/rubric.service';
import { ProductStatusSelect } from '../product-status-select/product-status-select';
import { ProductStatusService } from '../../../../services/product-status';
import { AuthService } from '../../../../services/auth.service';
import { PreviewActivity } from '../preview-activity/preview-activity';

@Component({
	selector: 'app-activity-list',
	standalone: true,
	imports: [
		AngularModule,
		ZorroModule,
		LanguageSelectorComponent,
		ActivityFormComponent,
		TableFormContainer,
		CmsTable,
		RowColumn,
		HeaderColumn,
		SkillSummary,
		RubricLevelSummary,
		ProductStatusSelect,
		PreviewActivity,
	],
	templateUrl: './activity-list.component.html',
	styleUrl: './activity-list.component.scss',
})
export class ActivityListComponent {
	readonly loading = signal<boolean>(true);
	readonly activities = signal<Activity[]>([]);
	readonly uuidsInProgress = signal<string[]>([]);
	readonly editLanguage = signal<string>('en');
	readonly editActivity = signal<Activity | undefined>(undefined);
	readonly columns = signal<Column[]>([]);
	readonly isAdmin = signal<boolean>(false);
	readonly previewActivity = signal<Activity | undefined>(undefined);
	readonly isFormShowing = signal<boolean>(false);

	private sub: Subscription | undefined;

	constructor(
		public activityService: ActivityService,
		private authService: AuthService,
		public nav: NavigationService,
		public adminService: AdminService,
		public clipboardService: ClipboardService,
		private inProgressService: InProgressService,
		public skillService: SkillService,
		public rubricService: RubricService,
		private productStatusService: ProductStatusService
	) {
		this.sub = this.activityService.activities$.subscribe((activities: Activity[]) => {
			void this.applyActivities(activities);

			this.loading.set(false);
		});

		this.authService.isAdmin$().then((isAdmin: boolean) => {
			this.isAdmin.set(isAdmin);
			this.updateTableDetails();
		});
	}

	private async updateTableDetails(): Promise<void> {
		const productStatuses: ProductStatus[] | undefined = this.adminService.selectedCourse$.value?.product_name
			? this.productStatusService.getByProductName(this.adminService.selectedCourse$.value?.product_name) || []
			: [];

		const columns: Column[] = [];

		if (this.isAdmin()) {
			columns.push(ENABLE_CHECKBOX_COLUMN);
		}

		columns.push(
			ENABLE_DISPLAY_ORDER_COLUMN,
			{
				key: 'activityName',
				name: 'Name',
				sortOrder: null,
				sortFn: (a: Activity, b: Activity) => a.active_text?.name.localeCompare(b.active_text?.name || '') || 0,
				sortDirections: ['ascend', 'descend', null],
				filterMultiple: true,
				filterOptions: this.activities().map(row => ({
					text: row.active_text?.name || '',
					value: row.active_text?.name || '',
				})),
				filterFn: (list: string[], item: Activity) =>
					list.some(name => item.active_text?.name.indexOf(name) !== -1),
			},
			{
				key: 'skillSummaries',
				name: 'Skill',
				sortOrder: null,
				sortFn: (a: Activity, b: Activity) => a.first_skill_code?.localeCompare(b.first_skill_code || '') || 0,
				sortDirections: ['ascend', 'descend', null],
				filterMultiple: true,
				filterOptions: [
					...new Set(
						this.activities()
							.map(row => row.first_skill_code || '')
							.filter(text => text !== '')
					),
				]
					.sort((a, b) => a.localeCompare(b))
					.map(text => ({
						text: TextUtils.upperCaseFirstLetter(text),
						value: text,
					})),
				filterFn: (list: string[], item: Activity) =>
					list.some(name => item.first_skill_code?.indexOf(name) !== -1),
			},
			{
				key: 'rubricLevelSummaries',
				name: 'Rubric Level',
				sortOrder: null,
				sortFn: (a: Activity, b: Activity) =>
					a.first_rubric_level?.toString().localeCompare(b.first_rubric_level?.toString() || '') || 0,
				sortDirections: ['ascend', 'descend', null],
				filterMultiple: true,
				filterOptions: [
					...new Set(
						this.activities()
							.map(row => row.first_rubric_level?.toString() || '')
							.filter(text => text !== '')
					),
				]
					.sort((a, b) => a.localeCompare(b))
					.map(text => ({
						text,
						value: text,
					})),
				filterFn: (list: string[], item: Activity) =>
					list.some(name => item.first_rubric_level?.toString().indexOf(name) !== -1),
			},
			{
				key: 'status',
				name: 'Status',
				sortOrder: null,
				sortFn: (a: Activity, b: Activity) => {
					const aStatus = productStatuses.find(ps => ps.uuid === a.status_uuid)?.status || '';
					const bStatus = productStatuses.find(ps => ps.uuid === b.status_uuid)?.status || '';
					return aStatus.localeCompare(bStatus);
				},
				sortDirections: ['ascend', 'descend', null],
				filterMultiple: true,
				filterOptions: [...new Set(productStatuses.map(row => row.status || '').filter(text => text !== ''))]
					.sort((a, b) => a.localeCompare(b))
					.map(text => ({
						text,
						value: text,
					})),
				filterFn: (list: string[], item: Activity) => {
					const activityStatusUuid: string | null | undefined = item.status_uuid;
					const activityStatus = productStatuses.find(ps => ps.uuid === activityStatusUuid)?.status || '';
					return list.some(name => name === activityStatus);
				},
			}
		);

		columns.push(ENABLE_PREVIEW_COLUMN);

		if (this.isAdmin()) {
			columns.push(ENABLE_COPY_COLUMN);
			columns.push(ENABLE_UP_DOWN_COLUMN);
			columns.push(ENABLE_EDIT_COLUMN);
			columns.push(ENABLE_DELETE_COLUMN);
		}

		this.columns.set(columns);
	}

	public preview($event: unknown): void {
		const activity = $event as Activity | undefined;

		this.previewActivity.set(activity);
	}

	public select($event: unknown): void {
		const activity = $event as Activity;

		if (!this.adminService.selectedClient$.value) {
			throw new Error('No client selected');
		}

		if (!this.adminService.selectedCourse$.value) {
			throw new Error('No course selected');
		}

		if (!this.adminService.selectedUnit$.value) {
			throw new Error('No unit selected');
		}

		if (!this.adminService.selectedLesson$.value) {
			throw new Error('No lesson selected');
		}

		this.nav.navigate([
			'my',
			'admin',
			'lc',
			{
				clientUuid: this.adminService.selectedClient$.value.uuid,
				courseUuid: this.adminService.selectedCourse$.value.uuid,
				unitUuid: this.adminService.selectedUnit$.value.uuid,
				lessonUuid: this.adminService.selectedLesson$.value.uuid,
				activityUuid: activity.uuid,
			},
		]);
	}

	public copy($event: object): void {
		const activity = $event as Activity;
		this.clipboardService.copyActivity(activity);
	}

	public copyBulk($event: object[]): void {
		const activities = $event as Activity[];
		this.clipboardService.copyActivities(activities);
	}

	public edit($event: unknown): void {
		const activity = $event as Activity;
		this.editActivity.set(this.editActivity()?.uuid === activity.uuid ? undefined : activity);
		this.isFormShowing.set(this.editActivity() !== undefined);
	}

	public onShowForm(): void {
		this.isFormShowing.set(true);
	}

	public onHideForm(): void {
		this.isFormShowing.set(false);
		this.resetEditState();
	}

	private resetEditState(): void {
		this.editActivity.set(undefined);
		this.editLanguage.set(LanguageCode.EN);
	}

	public async delete$($event: unknown): Promise<void> {
		const activity = $event as Activity;
		this.loading.set(true);
		try {
			await this.activityService.delete$(activity.uuid);
			this.activities.set(this.activities().filter(a => a.uuid !== activity.uuid));
		} catch (error) {
			console.error('Error deleting activity:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public async moveUp$($event: unknown): Promise<void> {
		const activity = $event as Activity;
		if (!this.adminService.selectedLesson$.value) {
			throw new Error('No lesson selected');
		}
		this.loading.set(true);
		try {
			const updatedActivities = await this.activityService.moveUp$(
				activity.uuid,
				this.adminService.selectedLesson$.value.uuid
			);
			this.activities.set(updatedActivities);
		} catch (error) {
			console.error('Error moving activity up:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public async moveDown$($event: unknown): Promise<void> {
		const activity = $event as Activity;
		if (!this.adminService.selectedLesson$.value) {
			throw new Error('No lesson selected');
		}
		this.loading.set(true);
		try {
			const updatedActivities = await this.activityService.moveDown$(
				activity.uuid,
				this.adminService.selectedLesson$.value.uuid
			);
			this.activities.set(updatedActivities);
		} catch (error) {
			console.error('Error moving activity down:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public async onActivityFormSubmit$($event: any): Promise<void> {
		let submitSucceeded = false;
		this.loading.set(true);
		try {
			if (this.editActivity()) {
				await this.updateActivity$($event);
			} else {
				await this.createNewActivity$($event);
			}

			submitSucceeded = true;
		} catch (error) {
			console.error('Error submitting activity form:', error);
		} finally {
			this.loading.set(false);
			this.resetEditState();

			if (submitSucceeded) {
				this.isFormShowing.set(false);
			}
		}
	}

	private async applyActivities(activities: Activity[]): Promise<void> {
		this.activities.set(activities);
		await this.updateTableDetails();

		const activityUuids: string[] = activities.map(activity => activity.uuid);

		this.inProgressService.activitiesInProgress$(activityUuids).then((uuids: string[]) => {
			this.uuidsInProgress.set(uuids);
		});

		const currentEditActivity = this.editActivity();
		if (currentEditActivity) {
			const refreshedEditActivity = activities.find(activity => activity.uuid === currentEditActivity.uuid);
			this.editActivity.set(refreshedEditActivity);
		}
	}

	private async createNewActivity$($event: any): Promise<void> {
		if (!this.adminService.selectedLesson$.value) {
			throw new Error('No lesson selected');
		}

		await this.activityService.create$({
			lesson_uuid: this.adminService.selectedLesson$.value.uuid,
			default_lang_id: 'en',
			is_instruction: false,
			copied_from_uuid: null,
			activity_text: [
				{
					default_lang_id: 'en',
					name: $event.name,
					image_uri: $event.imageUri,
					description_short: $event.descriptionShort,
					description_html: $event.descriptionHtml,
					created: new Date(),
					updated: new Date(),
				},
			],
		});
	}

	private async updateActivity$($event: any): Promise<void> {
		const currentEditActivity = this.editActivity();
		if (!currentEditActivity) {
			throw new Error('No activity to update');
		}

		const activityTexts: ActivityText[] = currentEditActivity.activity_text;

		let activityText: ActivityText | undefined = activityTexts.find(
			text => text.default_lang_id === this.editLanguage()
		);

		if (!activityText) {
			activityText = {
				uuid: v4(),
				activity_uuid: currentEditActivity.uuid,
				default_lang_id: this.editLanguage(),
				name: $event.name,
				image_uri: $event.imageUri,
				description_short: $event.descriptionShort,
				description_html: $event.descriptionHtml,
				created: new Date(),
				updated: new Date(),
			};

			activityTexts.push(activityText);
		} else {
			activityText.name = $event.name;
			activityText.image_uri = $event.imageUri;
			activityText.description_short = $event.descriptionShort;
			activityText.description_html = $event.descriptionHtml;
			activityText.updated = new Date();
		}

		const props = Object.keys(EMPTY_ACTIVITY);
		const payload: Activity = _.pick(currentEditActivity, props) as Activity;

		await this.activityService.update$(currentEditActivity.uuid, {
			...payload,
			activity_text: activityTexts,
		});
	}

	public onEditLanguageChange(code: string): void {
		this.editLanguage.set(code);
	}

	public onProductStatusChange(activity: Activity, $event: unknown): void {
		const productStatus = $event as ProductStatus;

		this.activityService.update$(activity.uuid, {
			...activity,
			status_uuid: productStatus?.uuid ?? null,
		});
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}
}
